import { formatAud, formatPercent } from '@/pages/blueprint-lite/utils/formatters';

import type {
  CashflowResult,
  Citizenship,
  CurrencyCode,
  EmploymentType,
  PropertyUse,
  RuleItem,
  RulesResult,
  TaxResidency,
} from '../types';

export interface RulesInput {
  citizenship: Citizenship;
  currency: CurrencyCode;
  employment: EmploymentType;
  lvr: number;
  surplus: number;
  hasPurchase: boolean;
  passCount: number;
  isForeign: boolean;
  grossAud: number;
  liabilitiesMonthly: number;
  depositGap: number;
  bonusAud: number;
  baseAud: number;
  cashflow: CashflowResult | null;
  propertyUse: PropertyUse;
  taxResidency: TaxResidency;
}

interface RawRule {
  text: string;
  weight: number;
  active: boolean;
}

function pickTop(rules: RawRule[], n: number): RuleItem[] {
  return rules
    .filter((r) => r.active)
    .sort((a, b) => b.weight - a.weight)
    .slice(0, n)
    .map(({ text, weight }) => ({ text, weight }));
}

function pickStrengths(input: RulesInput): RuleItem[] {
  const rules: RawRule[] = [
    {
      active: input.citizenship === 'australian_citizen',
      weight: 10,
      text: `AU citizenship grants access to full lender panel — ${input.passCount} lender(s) available`,
    },
    {
      active: input.citizenship === 'australian_pr_visa',
      weight: 8,
      text: 'PR status maintains access to major banks at standard LVR thresholds',
    },
    {
      active: input.lvr > 0 && input.lvr <= 70,
      weight: 10,
      text: `Strong equity — LVR ${input.lvr.toFixed(1)}% well below 80%, all lender tiers open`,
    },
    {
      active: input.lvr > 70 && input.lvr <= 80,
      weight: 7,
      text: `LVR ${input.lvr.toFixed(1)}% in clean-credit band — no LMI`,
    },
    {
      active: input.surplus >= 3000,
      weight: 9,
      text: `Surplus ${formatAud(input.surplus)}/mo — strong serviceability`,
    },
    {
      active: input.surplus >= 1500 && input.surplus < 3000,
      weight: 6,
      text: `Surplus ${formatAud(input.surplus)}/mo passes APRA serviceability`,
    },
    {
      active: input.passCount >= 3,
      weight: 8,
      text: `${input.passCount} lenders pass — competition for rate/terms`,
    },
    {
      active: input.employment === 'payg',
      weight: 7,
      text: 'PAYG is the most straightforward income type',
    },
    {
      active:
        !!input.cashflow &&
        input.cashflow.afterTaxCashflow >= 0 &&
        input.propertyUse === 'investment',
      weight: 9,
      text: input.cashflow
        ? `Property cashflow positive after tax ${formatAud(input.cashflow.afterTaxCashflow)}/yr`
        : '',
    },
    {
      active:
        !!input.cashflow &&
        input.cashflow.negGearingOffset > 0 &&
        input.propertyUse === 'investment',
      weight: 7,
      text: input.cashflow
        ? `Negative gearing generates ${formatAud(input.cashflow.negGearingOffset)}/yr tax offset at ${formatPercent(input.cashflow.marginalRate)} marginal`
        : '',
    },
    {
      active: input.depositGap >= 5000,
      weight: 6,
      text: `Deposit funds sufficient — ${formatAud(input.depositGap)} surplus after stamp duty + fees`,
    },
    {
      active: input.grossAud >= 300_000,
      weight: 7,
      text: `Gross income ${formatAud(input.grossAud)} — strong serviceability position`,
    },
    {
      active: input.liabilitiesMonthly === 0,
      weight: 5,
      text: 'No declared liabilities — clean liability position',
    },
  ];
  return pickTop(rules, 3);
}

function pickConsiderations(input: RulesInput): RuleItem[] {
  const rules: RawRule[] = [
    {
      active: input.lvr > 90,
      weight: 10,
      text: `LVR ${input.lvr.toFixed(1)}% exceeds 90% — very few lenders; more deposit required`,
    },
    {
      active: input.lvr > 80 && input.lvr <= 90,
      weight: 9,
      text: `LVR ${input.lvr.toFixed(1)}% triggers LMI`,
    },
    {
      active: input.surplus < 0,
      weight: 10,
      text: 'Negative surplus — adjust price or deposit',
    },
    {
      active: input.surplus >= 0 && input.surplus < 1000,
      weight: 7,
      text: 'Thin surplus — borderline serviceability',
    },
    {
      active: input.isForeign,
      weight: 9,
      text: 'Foreign national — lender access restricted; specialist lenders only',
    },
    {
      active:
        input.currency !== 'AUD' &&
        input.currency !== 'SGD' &&
        input.currency !== 'HKD',
      weight: 7,
      text: `${input.currency} income shading reduces assessed income vs gross ${formatAud(input.grossAud)}`,
    },
    {
      active: input.currency === 'HKD' || input.currency === 'SGD',
      weight: 5,
      text: `${input.currency} shading (typically 70%) reduces assessed income`,
    },
    {
      active:
        input.employment === 'selfemployed' || input.employment === 'director',
      weight: 7,
      text: 'Self-employed / director — needs 2yr tax returns + financials',
    },
    {
      active: input.depositGap < 0 && input.hasPurchase,
      weight: 8,
      text: `Deposit shortfall ${formatAud(Math.abs(input.depositGap))} — covers stamp duty + fees`,
    },
    {
      active: input.bonusAud > input.baseAud * 0.4,
      weight: 6,
      text: 'High bonus reliance — lenders shade 50%–100% across panel',
    },
    {
      active: input.passCount === 1,
      weight: 7,
      text: 'Only one lender passes — concentration risk',
    },
    {
      active:
        !!input.cashflow &&
        input.cashflow.preTaxCashflow < -30000 &&
        input.propertyUse === 'investment',
      weight: 5,
      text: input.cashflow
        ? `Pre-tax cashflow loss ${formatAud(input.cashflow.preTaxCashflow)}/yr requires active cash management`
        : '',
    },
  ];
  return pickTop(rules, 3);
}

function taxNoteFor(input: RulesInput): string {
  const r = input.taxResidency;
  if (r === 'non_tax_resident' || r === 'part_year_tax_resident_leaving_aus') {
    return '12.5% Foreign Resident Capital Gains Withholding applies on disposal while non-resident; no access to the 50% CGT discount. Marginal-rate treatment begins at 30% with no tax-free threshold.';
  }
  if (r === 'part_year_tax_non_resident_returning_aus') {
    return 'Part-year apportionment applies: capital growth accrued as resident may qualify for the 50% CGT discount; growth as non-resident does not. Confirm residency dates with a tax agent before disposal.';
  }
  if (r === 'unsure') {
    return 'Tax residency at time of sale determines CGT treatment, withholding, and marginal-rate schedule. Confirm status with a tax agent before reliance on these figures.';
  }
  if (input.propertyUse === 'investment') {
    return 'Australian tax resident: 50% CGT discount eligible if held >12 months. Confirm residency status at time of sale.';
  }
  return 'Australian tax resident: main residence CGT exemption may apply; time abroad / rental periods can reduce it.';
}

export function computeRules(input: RulesInput): RulesResult {
  const strengths = pickStrengths(input);
  const considerations = pickConsiderations(input);
  const headline =
    input.passCount > 0
      ? `${input.passCount} lender(s) currently pass policy`
      : 'No lenders fully pass — review considerations';
  return {
    strengths,
    considerations,
    headline,
    taxNote: taxNoteFor(input),
  };
}
